/**
 * Disease Categories - Hierarchical disease classification
 *
 * AUTO-GENERATED by categorize_diseases.py
 * Based on real cellxgene diseases categorized by Gemini AI
 */

export interface DiseaseCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  diseases: string[];
}

/**
 * Disease category definitions
 * Each category maps to specific diseases available in cellxgene
 */
export const DISEASE_CATEGORIES: DiseaseCategory[] = [
  {
    id: 'cancer',
    name: 'Cancer',
    description: 'Malignant neoplasms and tumor-related conditions',
    icon: '🎗️',
    color: 'var(--error)',
    diseases: [
      "Breast Cancer",
      "Lung Cancer",
      "Colorectal Cancer",
      "Colon Adenocarcinoma",
      "Gastric Cancer",
      "Liver Cancer",
      "Ovarian Cancer",
      "Melanoma",
      "Uveal Melanoma",
      "Basal Cell Carcinoma",
      "Oropharynx Squamous Cell Carcinoma",
      "Pilocytic Astrocytoma"
    ],
  },
  {
    id: 'neurological',
    name: 'Neurological',
    description: 'Brain, nervous system, and neurodegenerative disorders',
    icon: '🧠',
    color: 'var(--origin-blue-500)',
    diseases: [
      "Alzheimer Disease",
      "Dementia",
      "Lewy Body Dementia",
      "Parkinson Disease",
      "Multiple Sclerosis",
      "Leukoencephalopathy, Diffuse Hereditary, With Spheroids 1"
    ],
  },
  {
    id: 'metabolic',
    name: 'Metabolic & Liver',
    description: 'Metabolic, endocrine, and liver disorders',
    icon: '⚡',
    color: 'var(--warning)',
    diseases: [
      "Type 2 Diabetes Mellitus",
      "Prediabetes Syndrome",
      "NAFLD",
      "Non-Alcoholic Fatty Liver Disease",
      "Liver Fibrosis",
      "Intestinal Failure–Associated Liver Disease"
    ],
  },
  {
    id: 'cardiovascular',
    name: 'Cardiovascular',
    description: 'Heart and blood vessel disorders',
    icon: '❤️',
    color: 'var(--error)',
    diseases: [
      "Heart Failure",
      "Dilated Cardiomyopathy",
      "Hypertrophic Cardiomyopathy",
      "Arrhythmogenic Right Ventricular Cardiomyopathy",
      "Non-Compaction Cardiomyopathy",
      "Acute Myocardial Infarction",
      "Myocardial Ischemia",
      "Atrial Fibrillation || Mitral Valve Insufficiency",
      "Heart Valve Disorder",
      "Congenital Heart Disease"
    ],
  },
  {
    id: 'infectious',
    name: 'Infectious',
    description: 'Viral, bacterial, and parasitic infections',
    icon: '🦠',
    color: 'var(--warning)',
    diseases: [
      "COVID-19",
      "Long COVID-19",
      "Post-COVID-19 Disorder",
      "HIV Infectious Disease",
      "HIV Infectious Disease || Visceral Leishmaniasis",
      "HIV Infectious Disease || Leishmaniasis",
      "Influenza"
    ],
  },
  {
    id: 'autoimmune',
    name: 'Autoimmune & Inflammatory',
    description: 'Immune system and inflammatory conditions',
    icon: '🔥',
    color: 'var(--error)',
    diseases: [
      "Crohn Disease",
      "Ulcerative Colitis",
      "Inflammatory Bowel Disease",
      "Sjogren Syndrome",
      "Juvenile Dermatomyositis"
    ],
  },
  {
    id: 'respiratory',
    name: 'Respiratory',
    description: 'Lung and airway conditions',
    icon: '🫁',
    color: 'var(--info)',
    diseases: [
      "Cystic Fibrosis",
      "Respiratory Failure",
      "Pulmonary Emphysema"
    ],
  },
  {
    id: 'kidney',
    name: 'Kidney & Renal',
    description: 'Kidney and urinary system disorders',
    icon: '🫘',
    color: 'var(--info)',
    diseases: [
      "Acute Kidney Failure",
      "Chronic Kidney Disease",
      "Obstructive Nephropathy"
    ],
  },
  {
    id: 'gastrointestinal',
    name: 'Gastrointestinal',
    description: 'Digestive system disorders',
    icon: '🫃',
    color: 'var(--warning)',
    diseases: [
      "Gastritis",
      "Gastric Intestinal Metaplasia",
      "Barrett Esophagus"
    ],
  },
  {
    id: 'eye',
    name: 'Eye Diseases',
    description: 'Vision and ocular conditions',
    icon: '👁️',
    color: 'var(--info)',
    diseases: [
      "Cataract",
      "Macular Degeneration",
      "Open-Angle Glaucoma"
    ],
  },
  {
    id: 'dental',
    name: 'Dental & Oral',
    description: 'Dental and oral health conditions',
    icon: '🦷',
    color: 'var(--info)',
    diseases: [
      "Enamel Caries",
      "Pulpitis",
      "Periodontitis",
      "Gingivitis"
    ],
  },
  {
    id: 'hematological',
    name: 'Hematological',
    description: 'Blood and bone marrow disorders',
    icon: '🩸',
    color: 'var(--error)',
    diseases: [
      "Clonal Hematopoiesis"
    ],
  },
  {
    id: 'urological',
    name: 'Urological',
    description: 'Urinary and reproductive system conditions',
    icon: '🫀',
    color: 'var(--info)',
    diseases: [
      "Benign Prostatic Hyperplasia"
    ],
  },
  {
    id: 'other',
    name: 'Other Conditions',
    description: 'Additional medical conditions',
    icon: '🏥',
    color: 'var(--text-secondary)',
    diseases: [
      "Injury"
    ],
  }
];

/**
 * Helper function to find category for a given disease
 */
export const getCategoryForDisease = (diseaseName: string): DiseaseCategory | undefined => {
  return DISEASE_CATEGORIES.find((category) =>
    category.diseases.some((disease) =>
      disease.toLowerCase() === diseaseName.toLowerCase()
    )
  );
};

/**
 * Helper function to get all diseases across all categories
 */
export const getAllDiseases = (): string[] => {
  return DISEASE_CATEGORIES.flatMap((category) => category.diseases);
};

/**
 * Helper function to search diseases by query
 */
export const searchDiseases = (query: string): string[] => {
  const lowerQuery = query.toLowerCase();
  return getAllDiseases().filter((disease) =>
    disease.toLowerCase().includes(lowerQuery)
  );
};
